/**
 * Centralized date utilities.
 *
 * Philosophy:
 *  - All timestamps are stored and transferred as UTC (ISO 8601) by the server.
 *  - The `X-Client-Time` header carries the client's LOCAL time WITH its
 *    timezone offset (e.g. "2026-03-22T10:00:00.000-04:00"). This single
 *    header serves two purposes on the backend:
 *      1. `ClientTimeMiddleware` — parses it as a UTC Date for create/update
 *         timestamp defaults (`clientNow()`).
 *      2. `DateTimezoneInterceptor` — extracts the offset suffix (`-04:00`) to
 *         adjust date-only query strings ("2026-03-22") to local midnight before
 *         they become database range boundaries.
 *  - X-Timezone-Offset header is no longer needed and has been removed.
 *  - Never use `new Date()` directly in API calls or form defaults;
 *    use `nowLocal()` / `toUTCString()` instead.
 *  - Never display a raw ISO string; always pass through `formatDate()` or
 *    `formatDateTime()` so the user sees their local time.
 */

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Returns the client's UTC offset as an ISO 8601 suffix string.
 * JavaScript's `getTimezoneOffset()` is inverted vs ISO 8601:
 *   UTC−4 → getTimezoneOffset() = 240 → suffix "-04:00"
 *   UTC+5:30 → getTimezoneOffset() = -330 → suffix "+05:30"
 */
const getOffsetSuffix = (): string => {
  const offsetMinutes = new Date().getTimezoneOffset();
  const sign = offsetMinutes <= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

// ─── Sending to API ───────────────────────────────────────────────────────────

/**
 * Current time as a LOCAL ISO 8601 string with timezone offset embedded.
 * Used for the `X-Client-Time` header so the backend can both:
 *  - use it as a UTC timestamp for `clientNow()` defaults, and
 *  - extract the offset to anchor date-only filter strings to local midnight.
 *
 * @example (UTC−4 / America/Santo_Domingo)
 *   nowLocal() → "2026-03-22T10:00:00.000-04:00"
 */
export const nowLocal = (): string => {
  const now = new Date();
  const offset = getOffsetSuffix();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hh}:${mm}:${ss}.${ms}${offset}`;
};

/**
 * Convert any Date object to a UTC ISO 8601 string for transmission.
 */
export const toUTCString = (date: Date): string => date.toISOString();

/**
 * Parse a `YYYY-MM-DD` date-input string to a Date at **local** midnight.
 * `new Date('2026-10-31')` parses as UTC, which is the previous evening in
 * UTC−4 and shifts min/max day pickers by one day. Use this for any
 * DateInput minDate/maxDate built from a date-input string.
 */
export const parseLocalDateInput = (localDateStr: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(localDateStr)) {
    const [year, month, day] = localDateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(localDateStr);
};

/**
 * Convert a local `YYYY-MM-DD` date-input string to a UTC ISO 8601 string.
 * Interprets the date at local midnight so "today" from any timezone
 * round-trips back to the correct calendar day.
 *
 * @example (UTC−4)
 *   fromDateInput('2026-03-22') → '2026-03-22T04:00:00.000Z'
 */
export const fromDateInput = (localDateStr: string): string => {
  if (!localDateStr) return nowLocal();
  const [year, month, day] = localDateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toISOString();
};

/**
 * Convert a local `YYYY-MM-DD` date-input string to a UTC ISO 8601 string
 * pinned to the **end** of the local day (23:59:59.999). Use this for
 * "expires on" semantics where picking today should remain valid through
 * end-of-day local, not collapse to local midnight (already past).
 */
export const endOfDayFromDateInput = (localDateStr: string): string => {
  if (!localDateStr) return nowLocal();
  const [year, month, day] = localDateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};

// ─── Receiving from API (local-time display) ──────────────────────────────────

/**
 * Safely parse any UTC ISO string, Date, or null/undefined into a Date.
 * Returns null when the input is empty or invalid.
 */
export const parseDate = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Format a UTC ISO string for date-only display in the user's local timezone.
 *
 * @example
 *   formatDate('2026-03-22T04:00:00.000Z') → '22/03/2026'
 */
export const formatDate = (
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  },
  locale = 'es-DO'
): string => {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(locale, options);
};

/**
 * Format a UTC ISO string as date + time in the user's local timezone.
 * Canonical platform format: `dd/MM/YYYY HH:mm:ss` (24-hour, with
 * seconds). Built manually (not via Intl) so the output is stable
 * across locales — Intl.DateTimeFormat drifts (`es-DO` renders
 * `22/3/2026, 10:00`, `en-GB` adds a comma, etc.). Using `getDate`
 * / `getHours` / `getMinutes` / `getSeconds` guarantees the CLIENT
 * timezone regardless of where the UTC timestamp originated.
 *
 * @example
 *   formatDateTime('2026-03-22T14:00:00.000Z') → '22/03/2026 10:00:00'
 *   // (rendered in America/Santo_Domingo → UTC-4)
 */
export const formatDateTime = (
  value: string | Date | null | undefined
): string => {
  const d = parseDate(value);
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const MM = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const HH = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}/${MM}/${yyyy} ${HH}:${mm}:${ss}`;
};

/**
 * Format a UTC ISO string as time only in the user's local timezone.
 *
 * @example
 *   formatTime('2026-03-22T14:00:00.000Z') → '10:00 AM'
 */
export const formatTime = (
  value: string | Date | null | undefined,
  locale = 'es-DO'
): string => {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Convert a UTC ISO string to a `YYYY-MM-DD` value suitable for
 * `<input type="date">` using the user's local date (not UTC date).
 *
 * Without this, a UTC midnight timestamp renders as the previous day
 * in timezones west of UTC (e.g. America/Santo_Domingo, UTC−4).
 *
 * @example
 *   toDateInputValue('2026-03-22T04:00:00.000Z') → '2026-03-22' (UTC−4)
 */
export const toDateInputValue = (value?: string | Date | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ─── Elapsed / dwell time (restaurant service board) ───────────────────────────

/**
 * Whole minutes elapsed between a past ISO timestamp and `nowMs` (defaults to
 * the current wall clock). Returns 0 for a missing/invalid/future value so
 * callers never render a negative duration. Floors to whole minutes — a table
 * open for 89 s reads as 1m, matching how a host reads a clock.
 */
export const elapsedMinutes = (
  fromIso?: string | null,
  nowMs: number = Date.now()
): number => {
  const d = parseDate(fromIso);
  if (!d) return 0;
  const diffMs = nowMs - d.getTime();
  if (!Number.isFinite(diffMs) || diffMs <= 0) return 0;
  return Math.floor(diffMs / 60000);
};

/** Dwell-time thresholds (minutes) for the service-board open-time badge. */
export const DWELL_WARN_MINUTES = 30;
export const DWELL_ALERT_MINUTES = 60;

export type DwellLevel = 'ok' | 'warn' | 'alert';

/**
 * Classify an open-table duration so the floor node + drawer can color the
 * badge: green (`ok`) under 30m, amber (`warn`) 30–60m, red (`alert`) over 60m.
 * Staff scan for red to know which tables to turn.
 */
export const dwellLevel = (minutes: number): DwellLevel => {
  if (minutes >= DWELL_ALERT_MINUTES) return 'alert';
  if (minutes >= DWELL_WARN_MINUTES) return 'warn';
  return 'ok';
};
